import { get } from 'svelte/store'

import { analytics } from '@lib/mixpanel/mixpanel'
import { PassportEvents, dispatch } from '@lib/events/events'
import { userStore, isLoggedInStore, accessTokenStore, isLoggingInProcessStore } from '@packages/stores'
import { PassportLocalStorage } from '@packages/utils/enums'

import { sdk } from '@lib/sdk/sdk'
import { AUTH_PROVIDERS, OAUTH_LOGIN_TYPE, OAUTH_PASSWORDLESS_TYPES } from '@packages/utils/enums'
import { Passport } from '@src/Passport'
import { getAddress } from './blockchain'
import { configStore } from '@src/stores/config'

const EVM_LOGIN_SCOPE = 'openid profile profile.write email phone blockchain.evm.write blockchain.evm'
export async function login(
  this: Passport,
  provider: AUTH_PROVIDERS,
  {
    forceEmail,
    forcePhone,
    oauthType,
    passwordlessType,
  }: {
    forceEmail?: string
    forcePhone?: string
    oauthType?: OAUTH_LOGIN_TYPE
    passwordlessType?: OAUTH_PASSWORDLESS_TYPES
  } = {},
) {
  void analytics.track('cp_login_initiated', {
    chain: this.config.chainId,
    clientId: this.config.clientId,
    provider: provider,
    location: window.location.origin + window.location.pathname,
  })

  try {
    if (provider === AUTH_PROVIDERS.METAMASK) {
      await sdk.evm.metamask.login()
    } else if (provider === AUTH_PROVIDERS.OAUTH) {
      await sdk.oauth.login({
        scope: EVM_LOGIN_SCOPE,
        redirectUrl: window.location.href,
        ...(oauthType && { type: oauthType }),
        ...(passwordlessType && { passwordlessType }),
        ...(forceEmail && { forceEmail }),
        ...(forcePhone && { forcePhone }),
      })
    } else {
      throw new Error('Unknown provider')
    }
    this.init()
  } catch (err) {
    dispatch(PassportEvents.ERROR, { provider, error: err })
    throw err
  }
}

export async function jwtLogin(
  this: Passport,
  {
    validatorId,
    idToken,
    accessToken,
  }:
    | { validatorId: string; idToken: string; accessToken?: never }
    | { validatorId: string; accessToken: string; idToken?: never },
) {
  if (accessToken && idToken) {
    throw new Error('Only one of idToken or accessToken should be provided')
  }
  void analytics.track('cp_login_initiated', {
    chain: this.config.chainId,
    clientId: this.config.clientId,
    provider: 'OAUTH_JWT',
    location: window.location.origin + window.location.pathname,
  })

  try {
    isLoggingInProcessStore.set(true)
    await sdk.oauth.loginWithJwt({
      scope: EVM_LOGIN_SCOPE,
      validatorId,
      redirectUrl: window.location.href,
      ...(idToken ? { idToken } : { accessToken }),
    })
    await this.init()
  } catch (err) {
    dispatch(PassportEvents.ERROR, { provider: 'OAUTH_JWT', error: err })
    throw err
  } finally {
    isLoggingInProcessStore.set(false)
  }
}

export async function logout({ shouldRevokeSession = false } = {}) {
  try {
    const config = get(configStore)
    const user = get(userStore)
    analytics.track('cp_logout', {
      chain: config.chainId,
      clientId: config.clientId,
      address: await getAddress(),
      location: window.location.href,
      $email: user?.email,
      $phone: user?.phone,
    })
  } catch {
    /**/
  }

  localStorage.removeItem(PassportLocalStorage.QR_CODE)
  isLoggedInStore.set(false)
  userStore.set(null)
  accessTokenStore.set(null)
  if (shouldRevokeSession) {
    await sdk.oauth.revokeSession()
  }
  sdk.logout()
  dispatch(PassportEvents.LOGOUT)
}
